import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, X, Play, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";

interface TourStep {
  id: string;
  title: string;
  content: string;
  target?: string; // CSS selector
  position?: 'top' | 'bottom' | 'left' | 'right';
  action?: () => void;
}

interface TourGuideProps {
  isOpen: boolean;
  onClose: () => void;
  tourType: 'user' | 'admin';
}

const userTour: TourStep[] = [
  {
    id: 'dashboard',
    title: 'Welcome to Your Dashboard',
    content: 'This is your learning dashboard where you can see your progress, enrolled courses, and achievements.',
    target: '.dashboard-stats'
  },
  {
    id: 'courses',
    title: 'Browse Courses',
    content: 'Explore our comprehensive course catalog to find professional development opportunities.',
    target: '.course-catalog-nav'
  },
  {
    id: 'portfolio',
    title: 'Build Your Portfolio',
    content: 'Document your achievements, badges, and certificates in your professional portfolio.',
    target: '.portfolio-nav'
  },
  {
    id: 'surveys',
    title: 'Provide Feedback',
    content: 'Complete surveys and evaluations to help improve our programs.',
    target: '.surveys-nav'
  }
];

const adminTour: TourStep[] = [
  {
    id: 'admin-dashboard',
    title: 'Admin Dashboard Overview',
    content: 'Monitor system-wide metrics, user progress, and key performance indicators.',
    target: '.admin-dashboard'
  },
  {
    id: 'user-management',
    title: 'User Management',
    content: 'Manage users, roles, and permissions across your organization.',
    target: '.manage-users-nav'
  },
  {
    id: 'course-management', 
    title: 'Course Management',
    content: 'Create, edit, and manage professional development courses and content.',
    target: '.course-builder-nav'
  },
  {
    id: 'reporting',
    title: 'Advanced Reporting',
    content: 'Access comprehensive analytics and generate detailed reports.',
    target: '.reporting-nav'
  },
  {
    id: 'integrations',
    title: 'System Integrations',
    content: 'Configure integrations with SIS, SSO, and other district systems.',
    target: '.integrations-nav'
  }
];

export function TourGuide({ isOpen, onClose, tourType }: TourGuideProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isActive, setIsActive] = useState(false);
  
  const tour = tourType === 'admin' ? adminTour : userTour;

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setIsActive(false);
    }
  }, [isOpen]);

  const startTour = () => {
    setIsActive(true);
    setCurrentStep(0);
    highlightTarget(tour[0].target);
  };

  const nextStep = () => {
    if (currentStep < tour.length - 1) {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      highlightTarget(tour[newStep].target);
      if (tour[newStep].action) {
        tour[newStep].action?.();
      }
    } else {
      endTour();
    }
  };

  const previousStep = () => {
    if (currentStep > 0) {
      const newStep = currentStep - 1;
      setCurrentStep(newStep);
      highlightTarget(tour[newStep].target);
    }
  };

  const endTour = () => {
    setIsActive(false);
    removeHighlight();
    onClose();
  };

  const highlightTarget = (selector?: string) => {
    removeHighlight();
    if (selector) {
      const element = document.querySelector(selector);
      if (element) {
        element.classList.add('tour-highlight');
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const removeHighlight = () => {
    document.querySelectorAll('.tour-highlight').forEach(el => {
      el.classList.remove('tour-highlight');
    });
  };

  const currentTourStep = tour[currentStep];

  if (!isOpen) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="h-5 w-5 text-primary" />
              {tourType === 'admin' ? 'Admin Tour Guide' : 'User Tour Guide'}
            </DialogTitle>
          </DialogHeader>
          
          {!isActive ? (
            <div className="space-y-4">
              <p className="text-muted-foreground">
                Take a guided tour to learn about all the features and functionality 
                available to {tourType === 'admin' ? 'administrators' : 'users'}.
              </p>
              <div className="flex flex-col gap-2">
                <Button onClick={startTour} className="w-full">
                  <Play className="h-4 w-4 mr-2" />
                  Start Tour ({tour.length} steps)
                </Button>
                <Button variant="outline" onClick={onClose} className="w-full">
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>Step {currentStep + 1} of {tour.length}</span>
                <Button variant="ghost" size="sm" onClick={endTour}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="space-y-3">
                <h3 className="font-semibold text-lg">{currentTourStep.title}</h3>
                <p className="text-muted-foreground">{currentTourStep.content}</p>
              </div>
              
              <div className="flex justify-between">
                <Button 
                  variant="outline" 
                  onClick={previousStep}
                  disabled={currentStep === 0}
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Previous
                </Button>
                
                <Button onClick={nextStep}>
                  {currentStep === tour.length - 1 ? 'Finish' : 'Next'}
                  {currentStep !== tour.length - 1 && (
                    <ChevronRight className="h-4 w-4 ml-2" />
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tour highlight overlay styles */}
      <style>{`
        .tour-highlight {
          position: relative;
          z-index: 1000;
          box-shadow: 0 0 0 4px hsl(var(--primary)), 0 0 0 6px hsl(var(--primary) / 0.3);
          border-radius: 8px;
          transition: all 0.3s ease;
        }
        
        .tour-highlight::before {
          content: '';
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: -1;
          pointer-events: none;
        }
      `}</style>
    </>
  );
}